import { FC, useCallback, useEffect, useRef } from 'react';
import { atom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { MapRef } from 'react-map-gl';
import type { Map as MapboxMap } from 'mapbox-gl';
import Map from 'components/map/map';
import { useDataTableContext } from 'components/data-table';
import { getMapDefaultConfig } from 'components/map/atoms';
import { TcListingAreasLayer } from './tc-listing-areas-layer';
import { TcListingScenarioAreasLayer } from './tc-listing-scenario-areas-layer';
import { useTcTileUrls, type TcMapView } from './use-tc-map-source';
import type { TypeCurvesListingFilters } from './use-type-curves-listing';
import styles from './type-curves-listing.module.scss';

/**
 * The Type Curve listing map. Draws every item matching the current filters
 * (the tile resolves the full filtered set server-side), not just the visible
 * page, so the map shows the whole result while the table paginates it.
 *
 * `developmentAreas` is switched OFF here. The shared `<Map>` mounts its own
 * `DevelopmentAreasLayer` (map.tsx) off this config, and that layer serves the
 * UNSCOPED dev-areas tile — the same `development-areas` source-layer our
 * page-scoped layer reads. Left on, every horizontal unit in view would draw
 * underneath ours and a correctly-scoped map would look unfiltered.
 *
 * The storage key is suffixed `-v2` because the config persists per user: anyone
 * who loaded the old listing map holds the previous defaults in localStorage and
 * would never pick up this change.
 */
const listingMapConfigAtom = atomWithStorage(
  'insights-tc-listing-map-config-v2',
  getMapDefaultConfig({
    // Shallow spread in getMapDefaultConfig, so the whole node is required.
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
  })
);

export const tcListingMapRefAtom = atom<MapRef | null>(null);

/** Default camera before the page's extent is known — roughly CONUS. */
const DEFAULT_CENTER = { longitude: -98, latitude: 31, zoom: 4 };

export type ListingBbox = [number, number, number, number];

// Animated fit to a `[minLng, minLat, maxLng, maxLat]` extent, guarding NaNs.
function fitToBbox(map: MapboxMap, bbox: ListingBbox): void {
  const [minLng, minLat, maxLng, maxLat] = bbox;
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return;
  map.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    { padding: 40, maxZoom: 15 }
  );
}

interface ListingMapProps {
  /** Active listing view — picks the layer set and the tile path. */
  view: TcMapView;
  /**
   * The active listing filters. Sent on the tile url so the tile resolves and
   * draws the full matching set (every matching Type Curve Area, or every
   * matching scenario's areas), independent of table pagination.
   */
  filters: TypeCurvesListingFilters;
  /** Union extent of the page, framed on landing when no row is selected. */
  pageBbox?: ListingBbox | null;
  /**
   * Id of the selected row. Drives the camera rather than the bbox array itself:
   * a fresh array on every render would refit continuously and fight panning.
   */
  selectedRowId?: string | null;
  /** Extent of the selected row, or null when it has no geometry. */
  selectedBbox?: ListingBbox | null;
  /** Selected row's Type Curve Area id (Type Curves / Workspaces highlight). */
  selectedAreaId?: number | null;
  /** Selected scenario id (Scenarios highlight). */
  selectedScenarioId?: number | null;
  /** Map-feature click → owning row, per view. */
  onSelectAreaId?: (areaId: number) => void;
  onSelectScenarioId?: (scenarioId: number) => void;
}

export const ListingMap: FC<ListingMapProps> = ({
  view,
  filters,
  pageBbox = null,
  selectedRowId = null,
  selectedBbox = null,
  selectedAreaId = null,
  selectedScenarioId = null,
  onSelectAreaId,
  onSelectScenarioId,
}) => {
  const mapRef = useAtomValue(tcListingMapRefAtom);
  const { isFilterPanelOpen } = useDataTableContext();
  const tileUrls = useTcTileUrls(view, filters);

  // Resize map after filter panel open/close animation settles.
  // requestAnimationFrame waits for the DOM layout to update before resize.
  useEffect(() => {
    if (!mapRef) return;
    const frame = requestAnimationFrame(() => {
      mapRef.getMap()?.resize();
    });
    return () => cancelAnimationFrame(frame);
  }, [mapRef, isFilterPanelOpen]);

  // Frame the whole page on landing / page change, but only while no row is
  // selected — a row fit (below) takes precedence. Keyed off the tile urls so it
  // fits once per page, not on every render.
  const lastFittedPageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mapRef || !tileUrls || selectedRowId !== null) return;
    const key = tileUrls.join('|');
    if (lastFittedPageRef.current === key) return;
    if (!pageBbox) return;
    const map = mapRef.getMap();
    if (!map) return;
    lastFittedPageRef.current = key;
    fitToBbox(map, pageBbox);
  }, [mapRef, tileUrls, pageBbox, selectedRowId]);

  // Fit when the SELECTED ROW changes. Keyed off the id so re-renders that hand
  // down an equal-but-new bbox array don't yank the camera back.
  const lastFittedRowRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mapRef) return;
    if (selectedRowId === null) {
      lastFittedRowRef.current = null;
      return;
    }
    if (lastFittedRowRef.current === selectedRowId) return;

    // bbox is nullable — drafts with no Type Curve area and scenarios with no
    // area configs both ship null. Nothing to fit; leave the camera alone.
    if (!selectedBbox) return;

    const map = mapRef.getMap();
    if (!map) return;

    // Mark fitted only AFTER a fit actually happens. `selectedRowId` and
    // `selectedBbox` do not always land in the same render (legacy ReactDOM.render
    // does not batch every path), so claiming the row on an id-without-bbox pass
    // would permanently suppress the real fit that follows.
    lastFittedRowRef.current = selectedRowId;
    fitToBbox(map, selectedBbox);
  }, [mapRef, selectedRowId, selectedBbox]);

  // Reset-zoom control (the same one the details page map shows). Frames the
  // page's union extent, so it undoes a row zoom; CONUS when nothing has an
  // extent yet.
  const handleResetZoom = useCallback(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    if (pageBbox) {
      fitToBbox(map, pageBbox);
      return;
    }

    map.easeTo({
      center: [DEFAULT_CENTER.longitude, DEFAULT_CENTER.latitude],
      zoom: DEFAULT_CENTER.zoom,
    });
  }, [mapRef, pageBbox]);

  return (
    <div className={styles.mapContainer}>
      <Map
        longitude={DEFAULT_CENTER.longitude}
        latitude={DEFAULT_CENTER.latitude}
        zoom={DEFAULT_CENTER.zoom}
        mapConfigAtom={listingMapConfigAtom}
        refAtom={tcListingMapRefAtom}
        showControls
        showMapFilters
        onResetZoom={handleResetZoom}
      >
        {/*
          One layer set per view, rendered conditionally so a tab switch unmounts
          and remounts the <Source> instead of diffing it.
        */}
        {view === 'scenarios' ? (
          <TcListingScenarioAreasLayer
            tileUrls={tileUrls}
            selectedScenarioId={selectedScenarioId}
            onSelectScenarioId={onSelectScenarioId}
          />
        ) : (
          <TcListingAreasLayer
            tileUrls={tileUrls}
            selectedAreaId={selectedAreaId}
            onSelectAreaId={onSelectAreaId}
          />
        )}
      </Map>
    </div>
  );
};
